import type { ComponentType } from "react";
import {
  IconGitHub,
  IconX,
  IconLinkedIn,
  type IconProps,
} from "@/components/icons";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { fonts } from "@/theme/tokens";
import { brand } from "@/lib/brand";
import { Wordmark } from "@/components/common/Wordmark";
import { FooterNav } from "./FooterNav";

interface SocialLink {
  readonly label: string;
  readonly href: string;
  readonly icon: ComponentType<IconProps>;
}

// Placeholder destinations — swap for real profile URLs.
const socials: readonly SocialLink[] = [
  { label: "GitHub", href: "#", icon: IconGitHub },
  { label: "X", href: "#", icon: IconX },
  { label: "LinkedIn", href: "#", icon: IconLinkedIn },
];

const year = new Date().getFullYear();

/**
 * Footer as a colophon: an oversized serif wordmark spanning the top, the
 * sitemap columns, then a ruled bottom line with social links and copyright.
 * Language is switched from the header, so the footer carries no selector. No
 * contact channels that bypass the intake funnel — the only way forward
 * remains "Get Started".
 */
export function Footer() {
  return (
    <Box component="footer" sx={{ borderTop: "1px solid", borderColor: "divider" }}>
      <Container maxWidth="lg" sx={{ pt: { xs: 6, md: 10 } }}>
        <Wordmark fontSize="clamp(2.5rem, 6vw, 5rem)" />
      </Container>
      <FooterNav />
      <Container
        maxWidth="lg"
        sx={{
          py: { xs: 4, md: 5 },
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          rowGap: 3,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {socials.map(({ label, href, icon: Icon }) => (
            <IconButton
              key={label}
              component="a"
              href={href}
              aria-label={label}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
            >
              <Icon size={18} />
            </IconButton>
          ))}
        </Box>
        <Typography
          color="text.secondary"
          sx={{ fontFamily: fonts.mono, fontSize: "0.75rem", letterSpacing: "0.08em" }}
        >
          &copy; {year} {brand.name}
        </Typography>
      </Container>
    </Box>
  );
}
